import React, { useEffect, useState } from "react";
import {
  DollarSign, Mail, Phone, MapPin, Calendar, Inbox,
  MessageSquare, Edit, X, CheckCircle2, XCircle, Clock,
  RefreshCw, AlertCircle, ArrowLeft, Home, User, FileText,
  Scale, Building2, MessageCircle, Receipt, ChevronRight
} from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getContacts } from "../../../api/contact";
import { fetchOfferDetails } from "../../analytics/api/analyticsApi";
import { updateOffer } from "../../../api/propertyManagement";
import toast from "react-hot-toast";

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return d.toLocaleString("en-AU", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "numeric"
  });
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", maximumFractionDigits: 0
  }).format(num);
};

const STATUS_STYLES = {
  SUBMITTED: {
    cls: "bg-gray-100 text-gray-700 border-l-4 border-gray-400",
    icon: Clock,
    label: "Submitted",
    bg: "bg-gray-50"
  },
  UNDER_REVIEW: {
    cls: "bg-amber-50 text-amber-700 border-l-4 border-amber-500",
    icon: RefreshCw,
    label: "Under Review",
    bg: "bg-amber-50/30"
  },
  ACCEPTED: {
    cls: "bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500",
    icon: CheckCircle2,
    label: "Accepted",
    bg: "bg-emerald-50/30"
  },
  REJECTED: {
    cls: "bg-rose-50 text-rose-700 border-l-4 border-rose-500",
    icon: XCircle,
    label: "Rejected",
    bg: "bg-rose-50/30"
  },
  COUNTER_OFFER: {
    cls: "bg-blue-50 text-blue-700 border-l-4 border-blue-500",
    icon: DollarSign,
    label: "Counter Offer",
    bg: "bg-blue-50/30"
  },
  WITHDRAWN: {
    cls: "bg-slate-100 text-slate-600 border-l-4 border-slate-400",
    icon: AlertCircle,
    label: "Withdrawn",
    bg: "bg-slate-50"
  },
  EXPIRED: {
    cls: "bg-orange-50 text-orange-700 border-l-4 border-orange-500",
    icon: AlertCircle,
    label: "Expired",
    bg: "bg-orange-50/30"
  },
};

const STATUS_OPTIONS = ["SUBMITTED", "UNDER_REVIEW", "ACCEPTED", "REJECTED", "COUNTER_OFFER", "WITHDRAWN", "EXPIRED"];

const InfoRow = ({ icon: Icon, label, value, href }) => {
  const content = href ? (
    <a href={href} className="text-gray-900 hover:text-[#55C5D0] hover:underline transition-colors font-medium">
      {value}
    </a>
  ) : (
    <span className="text-gray-900 font-medium">{value}</span>
  );

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="w-5 h-5 text-gray-400 shrink-0 mt-0.5">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
        <div className="text-sm truncate">{content}</div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
    <div className="flex items-center gap-2 text-gray-500 mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-lg font-semibold text-gray-900">{value || "—"}</p>
  </div>
);

const SectionCard = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-200/80 shadow-lg shadow-gray-200/30 overflow-hidden ${className}`}>
    <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-[#55C5D0]" />}
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      </div>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

const OfferDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [offer, setOffer] = useState(location.state?.offer ?? null);
  const [loading, setLoading] = useState(!location.state?.offer);
  const [messagingBuyer, setMessagingBuyer] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ status: "", counter_amount: "", agent_notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location.state?.offer) {
      setOffer(location.state.offer);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const end = new Date().toISOString();
        const start = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();
        const data = await fetchOfferDetails(start, end, 500);
        const found = (data || []).find((o) => String(o.id) === String(id));
        if (!cancelled) {
          if (found) setOffer(found);
          else navigate("/offers", { replace: true });
        }
      } catch {
        if (!cancelled) navigate("/offers", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, location.state?.offer, navigate]);



  const renderPrice = (o) => {
    if (o.offer_amount_min != null && o.offer_amount_max != null) {
      return (
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold text-gray-900 tabular-nums">
            {formatMoney(o.offer_amount_min)}
          </span>
          <span className="text-gray-400">–</span>
          <span className="text-3xl font-semibold text-gray-900 tabular-nums">
            {formatMoney(o.offer_amount_max)}
          </span>
        </div>
      );
    }
    const amount = formatMoney(o.offer_amount ?? o.offer_amount_min ?? o.offer_amount_max);
    return (
      <span className="text-3xl font-semibold text-gray-900 tabular-nums">
        {amount || "N/A"}
      </span>
    );
  };

  const openEdit = () => {
    if (!offer) return;
    setEditForm({
      status: offer.status || "SUBMITTED",
      counter_amount: offer.counter_amount || "",
      agent_notes: offer.agent_notes || offer.agent_response || "",
    });
    setShowEditModal(true);
  };

  const loadOffer = async () => {
    try {
      const end = new Date().toISOString();
      const start = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();
      const data = await fetchOfferDetails(start, end, 500);
      const found = (data || []).find((o) => String(o.id) === String(offer.id));
      if (found) setOffer(found);
    } catch {
      // keep current offer
    }
  };

  const handleSaveEdit = async () => {
    if (!offer) return;
    setSaving(true);
    try {
      const payload = {
        status: editForm.status,
        agent_notes: editForm.agent_notes,
        counter_amount: editForm.status === "COUNTER_OFFER" ? (editForm.counter_amount || null) : null,
      };
      const res = await updateOffer(offer.id, payload);
      const data = res?.data || {};
      await loadOffer();
      toast.success("Offer updated successfully");
      if (data.email_sent) {
        toast.success("✉️ Buyer notified by email", { duration: 4000 });
      }
      setShowEditModal(false);
    } catch {
      toast.error("Failed to update offer");
    } finally {
      setSaving(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const s = STATUS_STYLES[status] || STATUS_STYLES.SUBMITTED;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold uppercase tracking-wider ${s.cls}`}>
        <s.icon className="w-3.5 h-3.5" />
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[#55C5D0]/20 border-t-[#55C5D0] animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-white/50 backdrop-blur-sm" />
          </div>
        </div>
        <p className="mt-6 text-sm font-medium text-gray-500">Loading offer details...</p>
      </div>
    );
  }

  if (!offer) return null;

  const buyerName = `${offer.first_name || ""} ${offer.last_name || ""}`.trim() || offer.email;
  const statusStyle = STATUS_STYLES[offer.status] || STATUS_STYLES.SUBMITTED;

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16">
        {/* Enhanced Header with Gradient */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <button
              type="button"
              onClick={() => navigate("/offers")}
              className="group flex items-center gap-2 text-sm text-gray-500 hover:text-[#55C5D0] transition-colors"
            >
              <div className="p-1.5 rounded-lg bg-white shadow-sm group-hover:shadow group-hover:bg-[#55C5D0]/5 transition-all">
                <ArrowLeft className="w-4 h-4" />
              </div>
              <span className="font-medium">Back to offers</span>
            </button>
            {offer.reference_number && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg shadow-sm">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium text-gray-600">Ref: {offer.reference_number}</span>
              </div>
            )}
          </div>

          {/* Hero Section - Redesigned */}
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xl shadow-gray-200/50 overflow-hidden">
            <div className={`h-2 ${statusStyle.bg}`} />
            <div className="p-6 sm:p-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <StatusBadge status={offer.status} />
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(offer.created_at)}
                    </span>
                  </div>

                  <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                    {buyerName}
                  </h1>

                  <div className="flex items-center gap-2 text-gray-500">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${offer.email}`} className="text-sm hover:text-[#55C5D0] transition-colors">
                      {offer.email}
                    </a>
                  </div>
                </div>

                <div className="flex flex-col items-start lg:items-end gap-4">
                  <div className="text-right">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Offer Amount</p>
                    {renderPrice(offer)}
                  </div>

                  <div className="flex gap-3 w-full lg:w-auto">

                    <button
                      onClick={openEdit}
                      className="flex-1 lg:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#55C5D0] text-white text-sm font-semibold hover:bg-[#4AADB7] transition-all duration-200 shadow-lg shadow-[#55C5D0]/25"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Offer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Deposit"
            value={formatMoney(offer.deposit_amount)}
            icon={Receipt}
          />
          <StatCard
            label="Settle Date"
            value={(() => {
              if (offer.custom_settlement_date) return formatDate(offer.custom_settlement_date);
              if (offer.settlement_days && offer.created_at) {
                const createdDate = new Date(offer.created_at);
                const settleDate = new Date(createdDate.getTime() + (offer.settlement_days * 24 * 60 * 60 * 1000));
                return formatDate(settleDate);
              }
              return "—";
            })()}
            icon={Calendar}
          />
          <StatCard
            label="Counter Offer"
            value={formatMoney(offer.counter_amount)}
            icon={DollarSign}
          />
        </div>

        {/* Two Column Layout for Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Buyer Details Section */}
            <SectionCard title="Buyer Information" icon={User}>
              <div className="space-y-1">
                <InfoRow icon={Mail} label="Email" value={offer.email} href={`mailto:${offer.email}`} />
                <InfoRow icon={Phone} label="Phone" value={offer.mobile_number} href={`tel:${offer.mobile_number}`} />
                <InfoRow icon={MapPin} label="Address" value={offer.home_address} />
              </div>
            </SectionCard>

            {/* Property Section */}
            <SectionCard title="Property Details" icon={Home}>
              {offer.property ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{offer.property.title || offer.property.address}</p>
                    {(offer.property.suburb || offer.property.state) && (
                      <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {offer.property.suburb}{offer.property.state && `, ${offer.property.state}`}
                      </p>
                    )}
                  </div>
                  {offer.property.description && (
                    <p className="text-sm text-gray-600 border-t border-gray-100 pt-4">{offer.property.description}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-2 text-center">No property linked to this offer</p>
              )}
            </SectionCard>

            {/* Conditions Section */}
            {offer.conditions && offer.conditions.length > 0 && (
              <SectionCard title="Conditions" icon={Scale}>
                <div className="space-y-4">
                  {offer.conditions.map((cond, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <p className="font-semibold text-gray-900 mb-2">
                        {(cond.type || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase())}
                      </p>
                      {cond.details && <p className="text-sm text-gray-600">{cond.details}</p>}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Messages Section */}
            {offer.message_to_agent && (
              <SectionCard title="Buyer's Message" icon={MessageCircle}>
                <div className="bg-[#55C5D0]/5 rounded-xl p-5 border border-[#55C5D0]/10">
                  <p className="text-gray-700 italic">"{offer.message_to_agent}"</p>
                </div>
              </SectionCard>
            )}

            {(offer.agent_notes || offer.agent_response) && (
              <SectionCard title="Agent Notes" icon={FileText}>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <p className="text-gray-700 whitespace-pre-wrap">{offer.agent_notes || offer.agent_response}</p>
                  {offer.responded_at && (
                    <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Responded {formatDateTime(offer.responded_at)}
                    </p>
                  )}
                </div>
              </SectionCard>
            )}
          </div>

          {/* Right Column - Additional Details */}
          <div className="space-y-6">
            {/* Buyer Section */}
            {offer.purchaser_details?.names?.length > 0 && (
              <SectionCard title="Buyer" icon={User}>
                <div className="space-y-3">
                  {offer.purchaser_details.names.map((name, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 rounded-full bg-[#55C5D0]/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#55C5D0]" />
                      </div>
                      <span className="font-medium text-gray-900">{name}</span>
                    </div>
                  ))}
                  {offer.purchaser_details.entity_name && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Entity</p>
                      <p className="font-medium text-gray-900">{offer.purchaser_details.entity_name}</p>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* Conveyancer Section */}
            {(offer.conveyancer_details?.name || offer.conveyancer_details?.email) && (
              <SectionCard title="Conveyancer" icon={Building2}>
                <div className="space-y-3">
                  {offer.conveyancer_details.name && (
                    <p className="font-medium text-gray-900">{offer.conveyancer_details.name}</p>
                  )}
                  {offer.conveyancer_details.email && (
                    <a href={`mailto:${offer.conveyancer_details.email}`}
                      className="inline-flex items-center gap-2 text-sm text-[#55C5D0] hover:underline">
                      <Mail className="w-4 h-4" />
                      {offer.conveyancer_details.email}
                    </a>
                  )}
                </div>
              </SectionCard>
            )}

            {/* Timeline Card */}
            <SectionCard title="Timeline" icon={Calendar}>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-[#55C5D0]" />
                  <div>
                    <p className="text-xs text-gray-500">Created</p>
                    <p className="text-sm font-medium text-gray-900">{formatDateTime(offer.created_at)}</p>
                  </div>
                </div>
                {offer.responded_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-gray-300" />
                    <div>
                      <p className="text-xs text-gray-500">Responded</p>
                      <p className="text-sm font-medium text-gray-900">{formatDateTime(offer.responded_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>
        </div>
      </div>

      {/* Enhanced Edit Modal */}
      {showEditModal && offer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
            <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-[#55C5D0]/10">
                    <Edit className="w-5 h-5 text-[#55C5D0]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Edit Offer</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {buyerName} • Ref: {offer.reference_number}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#55C5D0]" />
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3.5 text-sm font-medium text-gray-900 bg-white 
                           focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] 
                           transition-all shadow-sm hover:border-gray-300"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_STYLES[s]?.label || s}
                    </option>
                  ))}
                </select>
              </div>

              {editForm.status === "COUNTER_OFFER" && (
                <div className="space-y-2 animate-fadeIn">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#55C5D0]" />
                    Counter Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={editForm.counter_amount}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, counter_amount: e.target.value }))}
                      placeholder="950000"
                      className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3.5 text-sm font-medium 
                               text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 
                               focus:border-[#55C5D0] transition-all shadow-sm hover:border-gray-300"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#55C5D0]" />
                  Agent Note
                </label>
                <textarea
                  rows={4}
                  value={editForm.agent_notes}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, agent_notes: e.target.value }))}
                  placeholder="Add a note about this offer..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3.5 text-sm font-medium 
                           text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 
                           focus:border-[#55C5D0] transition-all resize-none placeholder:text-gray-400 
                           shadow-sm hover:border-gray-300"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={saving}
                  className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 text-sm font-semibold 
                           text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="flex-1 py-3.5 rounded-xl bg-[#55C5D0] text-white text-sm font-semibold 
                           hover:bg-[#4AADB7] transition-all disabled:opacity-50 shadow-lg 
                           shadow-[#55C5D0]/25 hover:shadow-xl hover:shadow-[#55C5D0]/30"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add animation keyframes to your global CSS or add to component */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OfferDetailPage;